import { createPath } from "@/service/routing/routing";
import { SuperLink as Link } from "@/components/common/super-link";
import { cn } from "@/lib/utils";
import { MedStatusBadge } from "@/components/meds/med-status-badge";
import { Badge } from "@/components/ui/badge";
import { InfoIcon } from "lucide-react";
import { ExpandableListWrapper } from "@/components/meds/med-group-card/components/expandable-list-wrapper";
import { MedSubgroupsList } from "@/components/meds/med-group-card/components/med-subgroups-list";
import { MedSubgroupsListMobile } from "@/components/meds/med-group-card/components/med-subgroups-list-mobile";
import { EXCLUDED_INN_NAME } from "@/components/meds/excluded-inn";
import {
  getMedGroupSubgroups,
  splitSubgroupsBySalesAvailability,
} from "@/components/meds/med-group-card/service";
import { trans } from "@/service/i18n/i18n";
import { ReactNode } from "react";

const headers = [
  "Nazwa handlowa",
  "Postać",
  "Dawka",
  "Opakowanie",
  "100%",
  "50%",
  "30%",
  "R",
  "Inne refundacje",
];

export function MedGroupCard({
  medGroup,
  headless = false,
  onlyNotForSale = false,
  onNavigate,
  footer,
}: {
  medGroup: MedGroupPreviewVM;
  headless?: boolean;
  onlyNotForSale?: boolean;
  onNavigate?: () => void;
  footer?: ReactNode;
}) {
  const { title, id, slug, featured, superGroup } = medGroup;
  const groupLink = createPath("meds.group.show", {
    slug,
    id,
  });

  const { all, forSale, notForSale } = splitSubgroupsBySalesAvailability(
    getMedGroupSubgroups(medGroup),
  );
  const hasOnlyNotForSale = forSale.length === 0 && notForSale.length > 0;
  const subgroups = onlyNotForSale
    ? notForSale
    : hasOnlyNotForSale
    ? all
    : forSale;
  // Not-for-sale variants are never refunded (50%, 30%, R, other).
  const withoutRefunds = onlyNotForSale || hasOnlyNotForSale;

  // Title original, kind 1.5x, opakowanie 1.5x.
  const trStyle = (isThead: boolean) =>
    cn(
      "grid w-fit min-w-full border-b border-[#E6E8ED] last:border-b-0 items-start",
      withoutRefunds
        ? "[grid-template-columns:180px_220px_100px_120px_60px] md:[grid-template-columns:31fr_38fr_18fr_18fr_7fr]"
        : "[grid-template-columns:120px_120px_80px_120px_repeat(4,60px)_182px] md:[grid-template-columns:20fr_18fr_12fr_18fr_repeat(4,7fr)_16fr]",
      !isThead && "px-4 py-2 [&:not(:last-child)]:border-b",
    );

  const desktopList = (
    <MedSubgroupsList
      subgroups={subgroups}
      groupLink={groupLink}
      superGroup={superGroup}
      headless={headless}
      trStyle={trStyle}
      withoutRefunds={withoutRefunds}
    />
  );

  return (
    <>
      <div className={cn("overflow-clip rounded-md border bg-white")}>
        {!headless && (
          <div
            className={
              "flex w-full flex-wrap items-center gap-2 p-4 xl:flex-nowrap"
            }
          >
            <MedStatusBadge status={medGroup.status} />
            <Link
              href={groupLink}
              className={`text-remedium-blue-hover text-base font-semibold text-black`}
              onClick={onNavigate}
            >
              {superGroup?.title ?? title}
            </Link>
            {!headless && featured && (
              <span className={`text-remedium-blue text-xs font-medium`}>
                &nbsp; REKLAMA
              </span>
            )}

            {medGroup.inns?.some(({ name }) => name !== EXCLUDED_INN_NAME) && (
              <div className={"ml-auto flex flex-wrap items-center gap-2"}>
                <span className={`text-xs font-normal text-black`}>
                  Preparat zawiera:
                </span>
                {medGroup.inns.map(({ slug, id, name }) => (
                  <Badge key={name} variant={"brandSoft"} asChild>
                    <Link
                      href={createPath("meds.inn.show", {
                        slug,
                        id,
                      })}
                    >
                      {name}
                    </Link>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Desktop version */}
        <div
          className={
            "default-scrollbar hidden overflow-auto sm:block xl:overflow-x-auto xl:overflow-y-auto"
          }
        >
          <div
            className={cn(
              trStyle(true),
              "h-10 border-t border-b px-4 py-2.5",
              headless && "border-t-0!",
            )}
          >
            {(withoutRefunds ? headers.slice(0, 5) : headers).map(
              (th, index) => (
                <div
                  key={th}
                  className={cn(
                    `text-muted-foreground flex text-xs font-semibold`,
                    index >= 4 && "items-center justify-center",
                  )}
                >
                  {th}
                </div>
              ),
            )}
          </div>
          {hasOnlyNotForSale && !onlyNotForSale ? (
            <ExpandableListWrapper>{desktopList}</ExpandableListWrapper>
          ) : (
            desktopList
          )}
        </div>

        {/* Mobile version */}
        <div className={cn("sm:hidden", headless ? "border-t-0" : "border-t")}>
          <MedSubgroupsListMobile
            subgroups={subgroups}
            groupLink={groupLink}
            superGroup={superGroup}
            headless={headless}
          />
        </div>

        {hasOnlyNotForSale && !onlyNotForSale && (
          <div
            className={
              "flex gap-2 border-t border-[#E6E8ED] bg-amber-50 px-4 py-2.5 text-xs font-semibold text-amber-700"
            }
          >
            <InfoIcon size={16} className={"min-w-4"} />
            {trans("meds.not_for_sale.only")}
          </div>
        )}
        {footer}
      </div>
      {headless && superGroup && !onlyNotForSale && (
        <div
          className={
            "mt-6 flex gap-2 rounded-md bg-teal-50 p-2 text-xs font-semibold text-teal-600"
          }
        >
          <InfoIcon size={16} className={"min-w-4"} />
          Poniższy opis odnosi się tylko do podświetlonych pozycji. Kliknij inny
          wariant, aby zobaczyć jego szczegółowe informacje.
        </div>
      )}
    </>
  );
}
